import { useForm } from "@tanstack/react-form";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MutationConfirmPreview } from "@/core/components/shared/preview-action";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { SearchableSelect } from "@/core/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { apiErrorHandler } from "@/core/api/api-error-handler";
import { isValidPhoneE164 } from "@/core/utils/func/input-normalization";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { logger } from "@/core/utils/func/logger";
import {
  OtpChallenge,
  type OtpPhoneVerificationProof,
} from "@/features/auth/components/otp-challenge";
import { useCreateParty } from "@/features/kyc/api/use-create-party";
import { useUpdateParty } from "@/features/kyc/api/use-update-party";
import type { PartyDto, PartyPayload } from "@/features/kyc/api/dto";
import { useNationalityOptions } from "@/features/kyc/components/parties/nationality-options";
import {
  createPartySchema,
  type PartyFormValues,
} from "@/features/kyc/schemas/party.schema";
import {
  KYC_DOCUMENT_TYPES,
  PARTY_TYPES,
  PREFERRED_LANGUAGES,
} from "@/features/shared/api/enums";

const EMPTY_VALUES: PartyFormValues = {
  partyType: "INDIVIDUAL",
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  nationality: "",
  phone: "",
  email: "",
  nationalIdType: undefined,
  nationalIdNumber: "",
  preferredLanguage: "FR",
  keycloakUserId: "",
  phoneVerificationToken: undefined,
};

/**
 * Nombre de vérifications SMS successives sans jeton renvoyé avant de
 * considérer que `PhoneVerification__KeyBase64` n'est pas configuré côté
 * serveur et de désactiver silencieusement le badge (voir item 20.9 du plan).
 */
const PHONE_VERIFICATION_SILENT_DISABLE_THRESHOLD = 2;

/**
 * Chaînes vides → undefined : les champs omis restent inchangés côté serveur.
 * Exception : `keycloakUserId` est obligatoire (guide v1.3) — le schéma zod
 * garantit une valeur non vide, on ne le transforme jamais en `undefined`.
 */
const toPayload = (values: PartyFormValues): PartyPayload =>
  Object.fromEntries(
    Object.entries(values).map(([k, v]) => [
      k,
      k === "keycloakUserId" ? v : v === "" ? undefined : v,
    ])
  ) as unknown as PartyPayload;

/** Format horaire local court pour le badge « vérifié jusqu'à ». */
const formatExpiry = (iso: string | null): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Formulaire de création/édition d'une identité (Party) — TanStack Form +
 * schéma zod mémoïsé sur la langue courante (règle projet : jamais
 * d'instance figée).
 */
export function PartyFormDialog({
  open,
  onOpenChange,
  party,
  onCreated,
  lockedKeycloakUserId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Présent = édition (PUT) ; absent = création (POST, idempotent). */
  party?: PartyDto;
  /** Appelé après une création réussie (wizard d'inscription, parcours 2.7). */
  onCreated?: (party: PartyDto) => void;
  /**
   * Création dans le parcours d'inscription : pré-remplit `keycloakUserId`
   * avec le compte créé à l'étape 1 et le verrouille en lecture seule.
   */
  lockedKeycloakUserId?: string;
}) {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue, le schéma zod traduit est reconstruit à chaque changement de langue (convention login.schema.ts).
  const schema = useMemo(() => createPartySchema(), [i18n.language]);

  const createParty = useCreateParty();
  const updateParty = useUpdateParty(party?.id ?? "");
  const isEdit = Boolean(party?.id);
  const pending = createParty.isPending || updateParty.isPending;
  const [preview, setPreview] = useState<PartyPayload | null>(null);
  const isKeycloakLocked = Boolean(lockedKeycloakUserId) && !isEdit;
  const nationalityOptions = useNationalityOptions();

  // --- Vérification du numéro (delta v3.4) ---
  const [otpOpen, setOtpOpen] = useState(false);
  const [phoneProof, setPhoneProof] =
    useState<OtpPhoneVerificationProof | null>(null);
  // Numéro effectivement vérifié : si l'opérateur modifie ensuite le champ, le
  // badge disparaît (le jeton n'est valable que pour l'adresse envoyée).
  const [verifiedPhone, setVerifiedPhone] = useState<string | null>(null);
  // Compte les succès SMS sans jeton — au-delà du seuil, on cache le bouton
  // (serveur sans `PhoneVerification__KeyBase64`, cas des déploiements UAT
  // antérieurs à v3.4).
  const [silentlyDisabled, setSilentlyDisabled] = useState(false);
  const [missingTokenCount, setMissingTokenCount] = useState(0);

  // Radix démonte le contenu du Dialog à la fermeture : à chaque ouverture,
  // le formulaire repart des valeurs courantes (édition) ou vides (création).
  const form = useForm({
    defaultValues: (party
      ? {
          ...EMPTY_VALUES,
          ...party,
          dateOfBirth: party.dateOfBirth ?? "",
          nationalIdType: party.nationalIdType,
          preferredLanguage: party.preferredLanguage ?? "FR",
          keycloakUserId: party.keycloakUserId,
        }
      : {
          ...EMPTY_VALUES,
          keycloakUserId: lockedKeycloakUserId ?? "",
        }) as PartyFormValues,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      const payload = toPayload(parsed.data);
      // Delta v3.4 : si l'opérateur a vérifié le numéro, on transmet le jeton
      // tant qu'il concerne le numéro effectivement saisi. Jeton expiré →
      // laissé en place : le serveur tranchera (422 explicite).
      if (
        !isEdit &&
        phoneProof &&
        verifiedPhone &&
        parsed.data.phone === verifiedPhone
      ) {
        payload.phoneVerificationToken = phoneProof.phoneVerificationToken;
      }
      setPreview(payload);
    },
  });

  const resetPhoneProof = () => {
    setPhoneProof(null);
    setVerifiedPhone(null);
  };

  const fieldError = (name: keyof PartyFormValues) => (props: { value: unknown }) => {
    const shape = schema.shape[name];
    const result = shape.safeParse(props.value ?? "");
    return result.success ? undefined : result.error.issues[0]?.message;
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t(isEdit ? "kyc.parties.edit" : "kyc.parties.create")}
          </DialogTitle>
        </DialogHeader>
        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field name="partyType" validators={{ onChange: fieldError("partyType") }}>
            {(field) => (
              <div className="space-y-1.5">
                <Label>{t("kyc.parties.form.partyType")}</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(v) => field.handleChange(v as PartyFormValues["partyType"])}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PARTY_TYPES.map((v) => (
                      <SelectItem key={v} value={v} className="notranslate">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          <form.Field name="preferredLanguage" validators={{ onChange: fieldError("preferredLanguage") }}>
            {(field) => (
              <div className="space-y-1.5">
                <Label>{t("kyc.parties.form.preferredLanguage")}</Label>
                <Select
                  value={field.state.value ?? ""}
                  onValueChange={(v) =>
                    field.handleChange(v as PartyFormValues["preferredLanguage"])
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PREFERRED_LANGUAGES.map((v) => (
                      <SelectItem key={v} value={v} className="notranslate">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          {(
            [
              ["firstName", "text"],
              ["lastName", "text"],
              ["dateOfBirth", "date"],
              ["email", "email"],
              ["nationalIdNumber", "text"],
            ] as const
          ).map(([name, type]) => (
            <form.Field key={name} name={name} validators={{ onChange: fieldError(name) }}>
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor={name}>{t(`kyc.parties.form.${name}`)}</Label>
                  <Input
                    id={name}
                    type={type}
                    className="notranslate"
                    placeholder={t(
                      `kyc.parties.form.${name}Placeholder` as TFunctionType
                    )}
                    value={(field.state.value as string) ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value as never)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>
          ))}

          {/* Téléphone + bouton de vérification SMS (delta v3.4) */}
          <form.Field name="phone" validators={{ onChange: fieldError("phone") }}>
            {(field) => {
              const rawPhone = ((field.state.value as string) ?? "").trim();
              const isVerified =
                !isEdit &&
                phoneProof !== null &&
                verifiedPhone !== null &&
                rawPhone === verifiedPhone;
              const canVerify =
                !isEdit && !silentlyDisabled && isValidPhoneE164(rawPhone);
              return (
                <div className="space-y-1.5">
                  <Label htmlFor="phone">
                    {t("kyc.parties.form.phone")}
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="phone"
                      type="tel"
                      className="notranslate"
                      placeholder={t("kyc.parties.form.phonePlaceholder")}
                      value={(field.state.value as string) ?? ""}
                      onBlur={field.handleBlur}
                      onChange={(e) => {
                        const next = e.target.value;
                        field.handleChange(next as never);
                        // Si l'opérateur modifie le numéro après vérification,
                        // le jeton n'est plus valable pour la nouvelle valeur.
                        if (verifiedPhone && next.trim() !== verifiedPhone) {
                          resetPhoneProof();
                        }
                      }}
                    />
                    {!isEdit && !silentlyDisabled ? (
                      isVerified ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="shrink-0"
                          disabled
                        >
                          <CheckCircle2 className="mr-2 h-4 w-4 text-success" />
                          <span className="notranslate">
                            {phoneProof?.phoneVerificationTokenExpiresAt
                              ? t(
                                  "kyc.parties.form.phoneVerification.verifiedUntil"
                                ).replace(
                                  "{{time}}",
                                  formatExpiry(
                                    phoneProof.phoneVerificationTokenExpiresAt
                                  )
                                )
                              : t(
                                  "kyc.parties.form.phoneVerification.verified"
                                )}
                          </span>
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="shrink-0"
                          disabled={!canVerify}
                          onClick={() => setOtpOpen(true)}
                        >
                          <ShieldCheck className="mr-2 h-4 w-4" />
                          {t("kyc.parties.form.phoneVerification.verify")}
                        </Button>
                      )
                    ) : null}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t("kyc.parties.form.phoneVerification.hint")}
                  </p>
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              );
            }}
          </form.Field>

          <form.Field name="nationality" validators={{ onChange: fieldError("nationality") }}>
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="nationality">{t("kyc.parties.form.nationality")}</Label>
                <SearchableSelect
                  id="nationality"
                  options={nationalityOptions}
                  value={(field.state.value as string) ?? ""}
                  onValueChange={(v) => field.handleChange(v as never)}
                  placeholder={t("kyc.parties.form.nationalityPlaceholder")}
                  searchPlaceholder={t("searchPlaceholder")}
                  emptyMessage={t("emptyMessage")}
                  noTranslate
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field
            name="keycloakUserId"
            validators={{ onChange: fieldError("keycloakUserId") }}
          >
            {(field) => (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="keycloakUserId">
                  {t("kyc.parties.form.keycloakUserId")}{" "}
                  <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="keycloakUserId"
                  className="notranslate"
                  placeholder={t("kyc.parties.form.keycloakUserIdPlaceholder")}
                  value={field.state.value ?? ""}
                  readOnly={isKeycloakLocked}
                  aria-readonly={isKeycloakLocked}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  {t("kyc.parties.form.keycloakUserIdHint")}
                </p>
              </div>
            )}
          </form.Field>

          <form.Field name="nationalIdType" validators={{ onChange: fieldError("nationalIdType") }}>
            {(field) => (
              <div className="space-y-1.5">
                <Label>{t("kyc.parties.form.nationalIdType")}</Label>
                <Select
                  value={field.state.value ?? ""}
                  onValueChange={(v) =>
                    field.handleChange(v as PartyFormValues["nationalIdType"])
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KYC_DOCUMENT_TYPES.map((v) => (
                      <SelectItem key={v} value={v} className="notranslate">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          <DialogFooter className="sm:col-span-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? <ButtonSpinner /> : null}
              {t("common.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    {/* Dialog de vérification SMS — delta v3.4 (item 20.9 du plan) */}
    <Dialog
      open={otpOpen}
      onOpenChange={(isOpen) => {
        setOtpOpen(isOpen);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("kyc.parties.form.phoneVerification.dialogTitle")}
          </DialogTitle>
        </DialogHeader>
        {otpOpen ? (
          <OtpChallenge
            channel="SMS"
            recipientAddress={(form.getFieldValue("phone") as string) ?? ""}
            preferredLanguage={
              ((form.getFieldValue("preferredLanguage") as string) ??
                "FR") as "FR" | "EN" | "ZH"
            }
            onVerified={(proof) => {
              const phoneValue = (
                (form.getFieldValue("phone") as string) ?? ""
              ).trim();
              if (proof) {
                setPhoneProof(proof);
                setVerifiedPhone(phoneValue);
                setMissingTokenCount(0);
              } else {
                // Succès OTP mais sans jeton : serveur sans la clé configurée.
                // On compte, et au seuil on cache silencieusement le bouton
                // pour ne pas polluer l'UX d'un opérateur BO.
                const next = missingTokenCount + 1;
                setMissingTokenCount(next);
                if (next >= PHONE_VERIFICATION_SILENT_DISABLE_THRESHOLD) {
                  setSilentlyDisabled(true);
                  logger.info(
                    "phoneVerificationToken absent après 2 vérifications SMS — bouton désactivé (serveur sans PhoneVerification__KeyBase64 ?)",
                    { prefix: "kyc" }
                  );
                }
              }
              setOtpOpen(false);
            }}
          />
        ) : null}
      </DialogContent>
    </Dialog>
    <MutationConfirmPreview
      open={preview !== null}
      onOpenChange={(isOpen) => {
        if (!isOpen) setPreview(null);
      }}
      data={preview}
      title={t(
        isEdit
          ? "common.mutationPreview.updateTitle"
          : "common.mutationPreview.createTitle"
      )}
      description={t(
        isEdit
          ? "common.mutationPreview.updateDescription"
          : "common.mutationPreview.createDescription"
      )}
      t={(key) => t(key as never)}
      pending={pending}
      onConfirm={() => {
        if (!preview) return;
        const mutation = isEdit ? updateParty : createParty;
        mutation.mutate(preview, {
          onSuccess: (data) => {
            notifSilence(
              t(
                isEdit
                  ? "kyc.parties.feedback.updated"
                  : "kyc.parties.feedback.created"
              )
            );
            if (!isEdit && data && typeof data === "object" && "id" in data) {
              onCreated?.(data as unknown as PartyDto);
            }
            setPreview(null);
            onOpenChange(false);
          },
          onError: (error) => notifSilenceError(apiErrorHandler(error)),
        });
      }}
    />
    </>
  );
}
